import React, { RefObject, useRef, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';
import { GLOBAL_PERMISSIONS } from '@CONSTANTS/PERMISSIONS.constant';

import useGlobalPermissions from '@HOOKS/useGlobalPermissions';
import { useGetAllCountriesAdmin } from '@SERVICES';

import BasicInputValidatable from '@COMPONENTS/COMMON/inputs/input/BasicInput/BasicInputValidatable.component';
import HiddenFormButton from '@COMPONENTS/COMMON/buttons/HiddenFormButton';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import RoundedCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/RoundedCheckbox';
import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';

import styles from './UserInvitationForm.module.scss';

const cx: CX = classnames.bind(styles);

const permissions = [
    {
        id: 1,
        name: 'Can create documents',
        codename: GLOBAL_PERMISSIONS.CAN_CREATE_DOCUMENTS,
    },
    {
        id: 2,
        name: 'Can manage vendors',
        codename: GLOBAL_PERMISSIONS.CAN_MANAGE_VENDORS,
    },
    {
        id: 3,
        name: 'Can view logs',
        codename: GLOBAL_PERMISSIONS.CAN_VIEW_LOGS,
    },
    {
        id: 4,
        name: 'Can manage users',
        codename: GLOBAL_PERMISSIONS.CAN_MANAGE_PERMISSIONS,
    },
];

enum Form {
    EMAIL = 'email',
    FIRST_NAME = 'firstName',
    LAST_NAME = 'lastName',
    FULL_NAME = 'fullName',
    USER_TYPE = 'userType',
    MOBILE_PHONE = 'mobilePhone',
    OFFICE_PHONE = 'officePhone',
    COUNTRY = 'country',
    TITLE = 'title',
}

enum Step {
    BASIC_INFO = 1,
    PERMISSIONS = 2,
}

yup.setLocale({
    mixed: {
        required: () => 'This field is required',
    },
});

const schema = yup.object({
    [Form.EMAIL]: yup.string().email('Invalid email format').required(),
    [Form.FIRST_NAME]: yup.string().required(),
    [Form.LAST_NAME]: yup.string().required(),
    [Form.FULL_NAME]: yup.string().required(),
    [Form.USER_TYPE]: yup.string(),
    [Form.MOBILE_PHONE]: yup.string(),
    [Form.OFFICE_PHONE]: yup.string(),
    [Form.COUNTRY]: yup.string(),
    [Form.TITLE]: yup.string(),
});

type UserInvitationFormValues = {
    [Form.EMAIL]: string;
    [Form.FIRST_NAME]: string;
    [Form.LAST_NAME]: string;
    [Form.FULL_NAME]: string;
    [Form.USER_TYPE]: string;
    [Form.MOBILE_PHONE]: string;
    [Form.OFFICE_PHONE]: string;
    [Form.COUNTRY]: string;
    [Form.TITLE]: string;
};

function UserInvitationForm(props: Props) {
    const {
        buttonTitle,
        isLoading,
        defaultValues,
        onSave,
    } = props;

    const {
        canManagePermissions,
    } = useGlobalPermissions();

    const { data: countries = [], isLoading: isLoadingCountries } = useGetAllCountriesAdmin();

    const [selectedPermissions, setSelectedPermissions] = useState<Set<GLOBAL_PERMISSIONS>>(new Set());
    const [currentStep, setCurrentStep] = useState<Step>(Step.BASIC_INFO);
    
    // Admin controls state
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [canAccessAppSettings, setCanAccessAppSettings] = useState(false);

    const formButtonRef: RefObject<HTMLButtonElement> = useRef(null);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        register,
        formState: { isValid },
    } = useForm<UserInvitationFormValues>({
        defaultValues: {
            [Form.EMAIL]: defaultValues[Form.EMAIL],
            [Form.FIRST_NAME]: defaultValues[Form.FIRST_NAME],
            [Form.LAST_NAME]: defaultValues[Form.LAST_NAME],
            [Form.FULL_NAME]: defaultValues[Form.FULL_NAME],
            [Form.USER_TYPE]: defaultValues[Form.USER_TYPE],
            [Form.MOBILE_PHONE]: defaultValues[Form.MOBILE_PHONE],
            [Form.OFFICE_PHONE]: defaultValues[Form.OFFICE_PHONE],
            [Form.COUNTRY]: defaultValues[Form.COUNTRY],
            [Form.TITLE]: defaultValues[Form.TITLE],
        },
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    const watchFirstName = watch(Form.FIRST_NAME);
    const watchLastName = watch(Form.LAST_NAME);

    // Auto-generate full name when first name or last name changes
    React.useEffect(() => {
        if (watchFirstName && watchLastName) {
            setValue(Form.FULL_NAME, `${watchFirstName} ${watchLastName}`.trim());
        }
    }, [watchFirstName, watchLastName, setValue]);

    const handlePermissionToggle = (permissionCodename: GLOBAL_PERMISSIONS) => {
        const newPermissions = new Set(selectedPermissions);
        if (newPermissions.has(permissionCodename)) {
            newPermissions.delete(permissionCodename);
        } else {
            newPermissions.add(permissionCodename);
        }
        setSelectedPermissions(newPermissions);
    };

    const handleNextStep = () => {
        if (currentStep === Step.BASIC_INFO && isValid) {
            setCurrentStep(Step.PERMISSIONS);
        }
    };

    const handlePreviousStep = () => {
        if (currentStep === Step.PERMISSIONS) {
            setCurrentStep(Step.BASIC_INFO);
        }
    };

    const onSubmit = (formValues: UserInvitationFormValues) => {
        const userData = {
            email: formValues.email,
            first_name: formValues.firstName,
            last_name: formValues.lastName,
            full_name: formValues.fullName,
            user_type: formValues.userType || 'Full-Time Employee',
            mobile_phone: formValues.mobilePhone,
            office_phone: formValues.officePhone,
            country: formValues.country,
            title: formValues.title,
        };

        // Convert selected permission codenames to permission IDs
        const permissionIds = Array.from(selectedPermissions)
            .map((codename) => permissions.find((p) => p.codename === codename)?.id)
            .filter((id): id is number => id !== undefined);

        onSave({
            users: [userData],
            permission_ids: permissionIds,
            is_superuser: isSuperuser,
            is_super_admin: isSuperAdmin,
            can_access_app_settings: canAccessAppSettings,
        });
    };

    const renderStepIndicator = () => (
        <div className={cx('step-indicator')}>
            <div className={cx('step', { active: currentStep === Step.BASIC_INFO })}>
                <div className={cx('step-number')}>1</div>
                <div className={cx('step-label')}>Basic Information</div>
            </div>
            <div className={cx('step-separator')} />
            <div className={cx('step', { active: currentStep === Step.PERMISSIONS })}>
                <div className={cx('step-number')}>2</div>
                <div className={cx('step-label')}>Permissions</div>
            </div>
        </div>
    );

    const renderBasicInfoStep = () => (
        <div className={cx('step-content')}>
            <div className={cx('input-row')}>
                <div className={cx('input-wrapper', 'half-width')}>
                    <BasicInputValidatable<UserInvitationFormValues>
                        label="First Name"
                        placeholder="First Name"
                        validation={{
                            name: Form.FIRST_NAME,
                            control,
                        }}
                    />
                </div>
                <div className={cx('input-wrapper', 'half-width')}>
                    <BasicInputValidatable<UserInvitationFormValues>
                        label="Last Name"
                        placeholder="Last Name"
                        validation={{
                            name: Form.LAST_NAME,
                            control,
                        }}
                    />
                </div>
            </div>

            <div className={cx('input-wrapper')}>
                <BasicInputValidatable<UserInvitationFormValues>
                    label="Full Name"
                    placeholder="Full Name (auto-generated)"
                    validation={{
                        name: Form.FULL_NAME,
                        control,
                    }}
                />
            </div>

            <div className={cx('input-wrapper')}>
                <BasicInputValidatable<UserInvitationFormValues>
                    label="Email"
                    placeholder="user@example.com"
                    validation={{
                        name: Form.EMAIL,
                        control,
                    }}
                />
            </div>

            <div className={cx('input-row')}>
                <div className={cx('input-wrapper', 'half-width')}>
                    <BasicInputValidatable<UserInvitationFormValues>
                        label="User Type"
                        placeholder="Full-Time Employee"
                        validation={{
                            name: Form.USER_TYPE,
                            control,
                        }}
                    />
                </div>
                <div className={cx('input-wrapper', 'half-width')}>
                    <BasicInputValidatable<UserInvitationFormValues>
                        label="Title"
                        placeholder="Job Title"
                        validation={{
                            name: Form.TITLE,
                            control,
                        }}
                    />
                </div>
            </div>

            <div className={cx('input-row')}>
                <div className={cx('input-wrapper', 'half-width')}>
                    <BasicInputValidatable<UserInvitationFormValues>
                        label="Mobile Phone"
                        placeholder="+1234567890"
                        validation={{
                            name: Form.MOBILE_PHONE,
                            control,
                        }}
                    />
                </div>
                <div className={cx('input-wrapper', 'half-width')}>
                    <BasicInputValidatable<UserInvitationFormValues>
                        label="Office Phone"
                        placeholder="+0987654321"
                        validation={{
                            name: Form.OFFICE_PHONE,
                            control,
                        }}
                    />
                </div>
            </div>

            <div className={cx('input-wrapper')}>
                <label htmlFor="country-select" className={cx('select-label')}>Country</label>
                <select
                    id="country-select"
                    className={cx('country-select')}
                    {...register(Form.COUNTRY)}
                    disabled={isLoadingCountries}
                >
                    <option value="">Select a country...</option>
                    {countries.map((country: any) => (
                        <option key={country.id} value={country.name}>
                            {country.name}
                        </option>
                    ))}
                </select>
                {isLoadingCountries && (
                    <div className={cx('loading-text')}>Loading countries...</div>
                )}
            </div>
        </div>
    );

    const renderPermissionsStep = () => (
        <div className={cx('step-content')}>
            <div className={cx('permissions-note')}>
                <div className={cx('note-icon')}>ℹ️</div>
                <div className={cx('note-text')}>
                    <strong>Note:</strong>
                    {' '}
                    Granular document permissions (which documents the user has access to) can be set in the individual documents after the user is created.
                </div>
            </div>

            <div className={cx('permissions-section')}>
                <div className={cx('permissions-title')}>
                    Admin Permissions
                </div>

                <div className={cx('admin-controls')}>
                    <div className={cx('is-admin')}>
                        <BasicCheckbox
                            title="Is admin"
                            checked={isSuperuser}
                            fontWeight={700}
                            onChange={() => {
                                setIsSuperuser((val: boolean) => !val);
                            }}
                        />
                    </div>

                    <div className={cx('app-settings-access')}>
                        <BasicCheckbox
                            title="Can access app settings"
                            checked={canAccessAppSettings}
                            fontWeight={700}
                            disabled={isSuperuser || isSuperAdmin}
                            onChange={() => {
                                setCanAccessAppSettings((val: boolean) => !val);
                            }}
                        />
                        <div className={cx('description')}>
                            Allows access to application configuration and settings
                        </div>
                    </div>

                    <div className={cx('super-admin')}>
                        <BasicCheckbox
                            title="Is super admin"
                            checked={isSuperAdmin}
                            fontWeight={700}
                            onChange={() => {
                                setIsSuperAdmin((val: boolean) => !val);
                            }}
                        />
                        <div className={cx('description')}>
                            Highest level access - can manage workspaces and system settings
                        </div>
                    </div>
                </div>

                <div className={cx('permissions-list')}>
                    <div className={cx('permissions-subtitle')}>
                        System Permissions
                    </div>
                    {permissions && permissions.length > 0 && (
                        <div className={cx('permissions-grid')}>
                            {permissions.map((permission) => {
                                const checked = selectedPermissions.has(permission.codename as GLOBAL_PERMISSIONS);
                                const disabled = isSuperuser || isSuperAdmin;

                                return (
                                    <div key={permission.id} className={cx('permission-item')}>
                                        <RoundedCheckbox
                                            color={COLOR['marriott-primary']}
                                            checked={isSuperuser || isSuperAdmin || checked}
                                            disabled={disabled}
                                            onChange={() => handlePermissionToggle(permission.codename as GLOBAL_PERMISSIONS)}
                                        />
                                        <span className={cx('permission-label')}>
                                            {permission.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className={cx('user-invitation-form')}>
            {renderStepIndicator()}
            
            <form onSubmit={handleSubmit(onSubmit)}>
                {currentStep === Step.BASIC_INFO && renderBasicInfoStep()}
                {currentStep === Step.PERMISSIONS && renderPermissionsStep()}

                <HiddenFormButton ref={formButtonRef} />
            </form>

            <div className={cx('step-navigation')}>
                {currentStep === Step.BASIC_INFO ? (
                    <BasicButton
                        title="Next: Permissions"
                        isProcessing={isLoading}
                        locked={!isValid}
                        style={{
                            height: 40,
                            width: '100%',
                            fontSize: 14,
                            backgroundColor: COLOR['marriott-primary'],
                        }}
                        onClick={handleNextStep}
                    />
                ) : (
                    <div className={cx('navigation-buttons')}>
                        <BasicButton
                            title="Back"
                            style={{
                                height: 40,
                                flex: 1,
                                fontSize: 14,
                                backgroundColor: COLOR['marriott-light-grey-2'],
                                color: COLOR['marriott-dark-grey'],
                            }}
                            onClick={handlePreviousStep}
                        />
                        <BasicButton
                            locked={!canManagePermissions}
                            title={buttonTitle}
                            isProcessing={isLoading}
                            style={{
                                height: 40,
                                flex: 1,
                                fontSize: 14,
                                backgroundColor: COLOR['app-green'],
                            }}
                            onClick={() => {
                                formButtonRef.current?.click();
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

UserInvitationForm.propTypes = {
    buttonTitle: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    defaultValues: PropTypes.exact({
        [Form.EMAIL]: PropTypes.string.isRequired,
        [Form.FIRST_NAME]: PropTypes.string.isRequired,
        [Form.LAST_NAME]: PropTypes.string.isRequired,
        [Form.FULL_NAME]: PropTypes.string.isRequired,
        [Form.USER_TYPE]: PropTypes.string.isRequired,
        [Form.MOBILE_PHONE]: PropTypes.string.isRequired,
        [Form.OFFICE_PHONE]: PropTypes.string.isRequired,
        [Form.COUNTRY]: PropTypes.string.isRequired,
        [Form.TITLE]: PropTypes.string.isRequired,
    }).isRequired,
    onSave: PropTypes.func.isRequired,
};

type Props = InferProps<typeof UserInvitationForm.propTypes>;

export default UserInvitationForm; 